import React, { useEffect, useMemo, useRef } from 'react';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Skeleton,
  Stack,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DocumentationAssetProvider } from '../DocumentationImage';
import { DocumentationMarkdown } from '../DocumentationMarkdown';
import { useProductDocumentation } from '../useProductDocumentation';
import {
  buildAnchorMap,
  buildDocumentationSections,
  DOCUMENTATION_FONT_FAMILY,
  findDocumentationChapter,
  keepSearch,
} from '../utils';

import { useDocsPanel } from './context';
import { DocsPanelHeader } from './DocsPanelHeader';
import { DocsPanelResizeHandle } from './DocsPanelResizeHandle';
import { usePanelSection } from './usePanelSection';
import { documentationUrl, scrollPanelTo } from './utils';

/**
 * A docs page docked beside the app. App links in it change the app next to
 * it (same tab); docs links stay in the panel. It scrolls on its own and
 * remembers the section being read.
 */
export const DocsPanel = () => {
  const panel = useDocsPanel();
  const { state } = panel;
  const navigate = useNavigate();
  const location = useLocation();
  const { documentation, error } = useProductDocumentation();
  const body = useRef<HTMLDivElement>(null);

  const chapters = useMemo(() => documentation?.chapters ?? [], [
    documentation,
  ]);
  const anchorMap = useMemo(
    () => buildAnchorMap(buildDocumentationSections(chapters)),
    [chapters],
  );
  const chapter = useMemo(
    () =>
      state.path
        ? findDocumentationChapter(chapters, documentationUrl(state.path))
        : undefined,
    [chapters, state.path],
  );
  const headings = useMemo(() => chapter?.headings ?? [], [chapter]);
  const steps = useMemo(() => headings.filter(heading => heading.level === 2), [
    headings,
  ]);
  const activeId = usePanelSection(body, headings);

  // The step is the top-level section that holds the heading being read.
  const activeIndex = headings.findIndex(heading => heading.id === activeId);
  const currentStep = Math.max(
    0,
    steps.reduce(
      (found, step, index) =>
        headings.indexOf(step) <= activeIndex ? index : found,
      -1,
    ),
  );

  const { setAnchor } = panel;
  useEffect(() => {
    if (activeId) setAnchor(activeId);
  }, [activeId, setAnchor]);

  // A newly shown page opens at its saved section (or the top). Images load
  // after the text and push content down, so settle the position again.
  const anchorRef = useRef(state.anchor);
  anchorRef.current = state.anchor;
  useEffect(() => {
    if (!chapter) return;
    const anchor = anchorRef.current;
    if (!anchor || !scrollPanelTo(body.current, anchor, false))
      body.current?.scrollTo({ top: 0 });
    if (!anchor) return;
    const settle = window.setTimeout(
      () => scrollPanelTo(body.current, anchor, false),
      500,
    );
    return () => window.clearTimeout(settle);
  }, [chapter]);

  // Links to another section of the same page scroll; others switch page.
  const openDocument = (path: string, anchor?: string) => {
    if (chapter && path === state.path) {
      if (anchor) scrollPanelTo(body.current, anchor, true);
      return;
    }
    panel.openDocument(path, anchor);
  };

  return (
    <Flex
      as="aside"
      aria-label="Guide"
      direction="column"
      // Pinned to the window, above the app's own floating layers, so the
      // header (and its close button) is always in view whatever the app
      // scrolls or overlays. Dialogs still open on top.
      position="fixed"
      top={0}
      right={0}
      bottom={0}
      zIndex="banner"
      w={`${state.width}px`}
      maxW="50vw"
      bg="white"
      borderLeft="1px solid"
      borderColor="gray.200"
      boxShadow="-4px 0 12px rgba(0, 0, 0, 0.04)"
      fontFamily={DOCUMENTATION_FONT_FAMILY}
    >
      <DocsPanelResizeHandle onResize={panel.setWidth} />
      <DocsPanelHeader
        title={chapter?.title ?? 'Guide'}
        steps={steps}
        currentStep={currentStep}
        onStep={id => scrollPanelTo(body.current, id, true)}
        onExpand={panel.expand}
        onClose={panel.close}
      />
      <Box ref={body} flex={1} overflowY="auto" px={5} py={4}>
        {error ? (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              The guide couldn't be loaded. Try again later.
            </AlertDescription>
          </Alert>
        ) : !documentation ? (
          <Stack spacing={3}>
            <Skeleton height="28px" />
            <Skeleton height="200px" />
          </Stack>
        ) : chapter ? (
          <DocumentationAssetProvider>
            <DocumentationMarkdown
              key={chapter.slug}
              chapter={chapter}
              anchorMap={anchorMap}
              variant="panel"
              // In the panel, app links change the app beside it.
              onAppLink={href => navigate(keepSearch(href, location.search))}
              onDocumentLink={openDocument}
            />
          </DocumentationAssetProvider>
        ) : (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              This page is no longer available.
            </AlertDescription>
          </Alert>
        )}
      </Box>
    </Flex>
  );
};
